// De los mensajes del chat a las tandas del lienzo —
// ADR-2026-09-16-el-lienzo-lee-los-mensajes.
//
// El lienzo no tiene fuente propia: aplana las tool cards que generan y ya dispararon.
// Todo lo que necesita ya viaja en la card y ya se persiste: el formato antes de
// generar (`input.format`), el "en vuelo" (`input.pending_generation`), las llegadas
// parciales del lote (`input.llegadas`, que escribe `LoteImagenesCard`) y los ids finales
// (`response.image_ids` / `response.video_id`). Nada de acá toca la red ni el DOM.
//
// EL TÍTULO de cada foto es lo que el usuario reconoce: el nombre del PRODUCTO de la
// card, si no el de la MODELO, y recién después lo que puso el agente (el título del
// lote, el arranque del prompt) — ADR-2026-09-16-el-titulo-de-la-foto-es-el-producto.
// Los nombres los resuelve quien llama (`nombres`), porque el catálogo vive en el
// entorno del chat; acá sólo hay ids.

import type { Punto } from './acomodo';
import type { TandaGuardada } from './persistenciaDelLienzo';
import type { V2ChatMessage, V2ToolCard } from '../../pages/v2/useV2Chat';
import { ratioDelFormatoElegido } from '../../pages/v2/modes/promptLibre/promptLibre';

export interface CeldaDelLienzo {
  /** `imageId` cuando ya existe; `${cardId}:pendiente:${i}` para un recuadro en vuelo. */
  id: string;
  imageId: string | null;
  esVideo: boolean;
  /** Título propio (un ítem del lote); sin él, el de la tanda. */
  titulo?: string;
  /**
   * Una imagen que NO es fila de `imagenes_generadas` (las propias del modo Diseño:
   * bases, inspiraciones, texturas), ya firmada o pública. Con `url` y sin `imageId` la
   * celda se dibuja con ella; sin ninguna de las dos es un recuadro en vuelo
   * (ADR-2026-09-17-el-lienzo-presta-tres-ranuras).
   */
  url?: string;
}

export interface TandaDelLienzo {
  /** El id de la card: una tanda es un pedido. */
  id: string;
  titulo: string;
  /** Epoch del disparo (reloj de la barra de carga); 0 si no se sabe. */
  creadaEn: number;
  ratio: number;
  esVideo: boolean;
  celdas: CeldaDelLienzo[];
  /**
   * La imagen del lienzo desde la que se pidió (Carrusel, Animar, Expansión desde el
   * visor): el acomodo la pone DEBAJO de ella, no a la derecha de todo
   * (ADR-2026-09-16-las-tandas-del-visor-aterrizan-en-el-lienzo).
   */
  origenId?: string;
  /**
   * Dónde aterrizó, decidido una vez al pedirla: con esto no vuelve a seguir a su foto de
   * origen (ADR-2026-09-17-la-tanda-aterriza-una-vez). Sólo las tandas guardadas lo tienen.
   */
  posicion?: Punto;
}

/** Cómo se llama un producto o una modelo por su id; `undefined` si no está. */
export interface NombresDelCatalogo {
  producto: (id: string) => string | undefined;
  modelo: (id: string) => string | undefined;
}

export const SIN_NOMBRES: NombresDelCatalogo = {
  producto: () => undefined,
  modelo: () => undefined,
};

/** Los kinds que generan algo que se pueda colgar del lienzo. */
export const KINDS_DEL_LIENZO: ReadonlySet<string> = new Set([
  'generate_image',
  'confirm_generate',
  'generar_lote_imagenes',
  'generate_video',
  // La imagen que devuelve el editor (ADR-2026-11-02-el-agente-edita-imagenes-con-ffmpeg): llega
  // resuelta, y en el lienzo se compara con la original como cualquier generación.
  'editor_de_imagenes',
]);

/** Tope del título, con la elipsis incluida: debajo de una foto de 220px entra eso. */
export const TITULO_MAX = 32;

const objeto = (v: unknown): Record<string, unknown> =>
  v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
const texto = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const ids = (v: unknown): string[] =>
  Array.isArray(v)
    ? v.filter((x) => typeof x === 'string' || typeof x === 'number').map(String)
    : [];
const lista = (v: unknown): Record<string, unknown>[] =>
  Array.isArray(v) ? v.map(objeto) : [];

export const recortarTitulo = (t: string): string =>
  t.length > TITULO_MAX ? `${t.slice(0, TITULO_MAX - 1).trimEnd()}…` : t;

/** El primer producto (o modelo) de una lista de referencias `{ source, product_id, model_id }`. */
function nombreDeReferencias(
  refs: Record<string, unknown>[],
  nombres: NombresDelCatalogo,
): string | undefined {
  const producto = refs.find((r) => texto(r.product_id));
  if (producto) {
    const n = nombres.producto(texto(producto.product_id));
    if (n) return n;
  }
  const modelo = refs.find((r) => texto(r.model_id));
  if (modelo) {
    const n = nombres.modelo(texto(modelo.model_id));
    if (n) return n;
  }
  return undefined;
}

/** Producto → modelo → lo que puso el agente → un rótulo por kind. Siempre recortado. */
export function tituloDeTanda(
  card: V2ToolCard,
  nombres: NombresDelCatalogo = SIN_NOMBRES,
): string {
  const input = objeto(card.input);
  let porCatalogo: string | undefined;
  if (card.kind === 'confirm_generate') {
    const productoId = ids(input.product_ids)[0];
    porCatalogo =
      (productoId ? nombres.producto(productoId) : undefined) ??
      (texto(input.model_id) ? nombres.modelo(texto(input.model_id)) : undefined);
  } else if (card.kind === 'generar_lote_imagenes') {
    const primero = lista(input.items)[0];
    porCatalogo = primero ? nombreDeReferencias(lista(primero.reference_images), nombres) : undefined;
  } else {
    porCatalogo = nombreDeReferencias(lista(input.reference_images), nombres);
  }
  if (porCatalogo) return recortarTitulo(porCatalogo);

  if (card.kind === 'generar_lote_imagenes') return recortarTitulo(texto(input.titulo) || 'Lote');
  if (card.kind === 'editor_de_imagenes') return recortarTitulo(texto(input.descripcion) || 'Edición');
  if (card.kind === 'confirm_generate') return 'Fotos de producto';
  const prompt = texto(input.prompt);
  if (prompt) return recortarTitulo(prompt);
  return card.kind === 'generate_video' ? 'Video' : 'Imágenes';
}

/**
 * En un lote terminado cada foto se llama como su ítem (`por_item` del resumen: el
 * nombre del producto o lo que puso el agente); el producto del ítem, si se conoce,
 * manda sobre la etiqueta.
 */
function titulosPorImagenDelLote(
  response: Record<string, unknown>,
  nombres: NombresDelCatalogo,
): Map<string, string> {
  const out = new Map<string, string>();
  for (const item of lista(response.por_item)) {
    const producto = texto(item.product_id) ? nombres.producto(texto(item.product_id)) : undefined;
    const titulo = producto || texto(item.etiqueta);
    if (!titulo) continue;
    for (const id of ids(item.image_ids)) out.set(id, recortarTitulo(titulo));
  }
  return out;
}

/**
 * La tanda de una card, o `null` si no va al lienzo: un kind que no genera, una
 * propuesta que todavía no disparó, o una card que terminó sin nada que mostrar.
 * Los recuadros en vuelo sólo existen mientras la card sigue `pending`: una card
 * cancelada o resuelta sin ids ya no espera nada.
 */
export function tandaDeCard(
  card: V2ToolCard,
  nombres: NombresDelCatalogo,
  ratioDe: (formato?: string) => number,
): TandaDelLienzo | null {
  if (!KINDS_DEL_LIENZO.has(card.kind)) return null;
  const input = objeto(card.input);
  const response = objeto(card.response);
  const esVideo = card.kind === 'generate_video';
  const listas = esVideo
    ? response.video_id !== undefined && response.video_id !== null
      ? [String(response.video_id)]
      : []
    : ids(response.image_ids);
  const pendiente = input.pending_generation ? objeto(input.pending_generation) : null;
  const enVuelo = card.status === 'pending' && pendiente !== null;
  if (listas.length === 0 && !enVuelo) return null;

  const celdas: CeldaDelLienzo[] = [];
  if (listas.length > 0) {
    const porImagen =
      card.kind === 'generar_lote_imagenes' ? titulosPorImagenDelLote(response, nombres) : null;
    for (const id of listas) {
      const titulo = porImagen?.get(id);
      celdas.push({ id, imageId: id, esVideo, ...(titulo ? { titulo } : {}) });
    }
  } else {
    const llegadas = ids(input.llegadas);
    for (const id of llegadas) celdas.push({ id, imageId: id, esVideo });
    const esperadas =
      typeof pendiente?.expected_count === 'number' ? pendiente.expected_count : 1;
    for (let i = 0; i < Math.max(0, esperadas - llegadas.length); i++) {
      celdas.push({ id: `${card.id}:pendiente:${i}`, imageId: null, esVideo });
    }
  }
  return {
    id: card.id,
    titulo: tituloDeTanda(card, nombres),
    creadaEn: typeof pendiente?.started_at === 'number' ? pendiente.started_at : 0,
    ratio: ratioDe(typeof input.format === 'string' ? input.format : undefined),
    esVideo,
    celdas,
  };
}

export function tandasDeMensajes(
  messages: V2ChatMessage[],
  nombres: NombresDelCatalogo = SIN_NOMBRES,
  ratioDe: (formato?: string) => number = ratioDelFormatoElegido,
): TandaDelLienzo[] {
  const out: TandaDelLienzo[] = [];
  for (const m of messages) {
    for (const c of m.toolCards ?? []) {
      const t = tandaDeCard(c, nombres, ratioDe);
      if (t) out.push(t);
    }
  }
  return out;
}

/**
 * ¿El chat está mostrando una generación en vuelo (un recuadro de "Generando…" en alguna
 * card)? Es lo que le dice al aviso flotante de "generando de fondo" que se calle: el
 * chat ya es una pantalla que muestra la carga
 * (ADR-2026-10-05-el-aviso-de-generando-no-se-monta-sobre-el-chat).
 */
export function hayGeneracionEnVuelo(messages: V2ChatMessage[]): boolean {
  return tandasDeMensajes(messages).some((t) => t.celdas.some((c) => !c.imageId && !c.url));
}

/**
 * Una tanda que NO viene de los mensajes: la pidió el usuario desde el visor prestado y
 * el lienzo la guarda por su cuenta (ADR-2026-09-16-las-tandas-del-visor-aterrizan-en-el-lienzo).
 * Mientras vuela, `total` dice cuántos recuadros mostrar además de lo que ya llegó.
 */
export function tandaDeGuardada(
  t: TandaGuardada,
  total = t.pendiente?.total ?? t.imageIds.length,
): TandaDelLienzo {
  const celdas: CeldaDelLienzo[] = t.imageIds.map((id) => ({ id, imageId: id, esVideo: t.esVideo }));
  for (let i = 0; i < Math.max(0, total - t.imageIds.length); i++) {
    celdas.push({ id: `${t.id}:pendiente:${i}`, imageId: null, esVideo: t.esVideo });
  }
  return {
    id: t.id,
    titulo: t.titulo,
    creadaEn: t.creadaEn,
    ratio: t.ratio,
    esVideo: t.esVideo,
    celdas,
    origenId: t.origenId,
    ...(t.posicion ? { posicion: t.posicion } : {}),
  };
}
